import React, {useEffect, useMemo, useState} from 'react';
import {t} from 'i18next';
import {Typography, Box} from '@mui/material';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import './CustomizeReviewerProductivityWidget.scss';
import ArrTreeView from '../../../common/arr-tree-view/ArrTreeView';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';
import {WidgetsHeader} from '../widgets-header/WidgetsHeader';
import ArrDatePicker from '../../../common/arr-date-picker/ArrDatePicker';
import {WidgetsCustomizationReviewerSection} from '../widgets-customization-reviewer-section/WidgetsCustomizationReviewerSection';
import {
  useReviewerList,
  useSaveReviewerProductivity,
} from '../../../../hooks/dashboard/useDashboard';
import {
  AssignmentRPW,
  ReviewProductivityRequest,
  Reviewer,
  ReviewerProductivityWidget,
} from '../../dashboardTypes';
import dayjs from 'dayjs';
import {SelectedDateType} from '../../../../shared/types';
import {useAssignmentsList} from '../../../../hooks/productions/useProductions';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {formatAssignmentsTreeData} from '../../../../utils/formatTagTreeData';

type ReviewerOption = 'todoDocs' | 'completeDocs' | 'specificReviewers';

type CustomizeReviewerProductivityWidgetProps = {
  showDrawer: boolean;
  handleDrawer: (value: React.SetStateAction<boolean>) => void;
  initialFilter?: ReviewProductivityRequest;
  onSuccess: (newData: ReviewerProductivityWidget) => void;
};
// Helpers
function toSelectedDateArray(s: string | null): SelectedDateType[] {
  if (!s) return [];
  const d = dayjs(s);
  return d.isValid() ? [d] : [];
}
function pickerValueToSelectedDateArray(
  value: string | [] | [string | undefined, string | undefined]
): SelectedDateType[] {
  const str = Array.isArray(value) ? value[0] : value;
  if (!str) return [];
  const d = dayjs(str);
  return d.isValid() ? [d] : [];
}
function selectedDateArrayToApi(arr: SelectedDateType[]): string | null {
  if (!arr.length) return null;
  const d = arr[0];
  if (!d) return null;
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
}

export const CustomizeReviewerProductivityWidget: React.FC<
  CustomizeReviewerProductivityWidgetProps
> = ({showDrawer, handleDrawer, initialFilter, onSuccess}) => {
  const normalized = React.useMemo<ReviewProductivityRequest>(
    () => ({
      assignments: initialFilter?.assignments ?? [],
      reviewers: initialFilter?.reviewers ?? [],
      reviewerFilterOptionId: initialFilter?.reviewerFilterOptionId ?? 0,
      fromDate: initialFilter?.fromDate ?? null,
      toDate: initialFilter?.toDate ?? null,
      widgetId: initialFilter?.widgetId ?? 0,
    }),
    [initialFilter]
  );

  const [startDate, setStartDate] = useState<SelectedDateType[]>(
    toSelectedDateArray(normalized.fromDate)
  );
  const [endDate, setEndDate] = useState<SelectedDateType[]>(
    toSelectedDateArray(normalized.toDate)
  );
  const {data: reviewerData, refetch} = useReviewerList();
  const reviewerList: Reviewer[] = useMemo(
    () => (Array.isArray(reviewerData) ? reviewerData : []),
    [reviewerData]
  );

  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const {data: assignmentData} = useAssignmentsList(
    Number(loginUserData?.securityGroupId)
  );
  const assignments = useMemo(() => {
    if (assignmentData?.length) {
      return formatAssignmentsTreeData(assignmentData);
    }
  }, [assignmentData]);
  const getInitialReviewerOption = (
    data: ReviewProductivityRequest
  ): ReviewerOption => {
    if (data.reviewers && data.reviewers.length > 0) {
      return 'specificReviewers';
    }
    return data.reviewerFilterOptionId === 6 ? 'completeDocs' : 'todoDocs';
  };

  const [reviewerOption, setReviewerOption] = useState<ReviewerOption>(
    getInitialReviewerOption(normalized)
  );

  const [reviewerFilterOptionId, setReviewerFilterOptionId] = useState<number>(
    normalized.reviewerFilterOptionId ?? 0
  );

  const [selectedReviewers, setSelectedReviewers] = useState<string[]>([]);

  const prefillReviewerNamesFromIds = React.useCallback(
    (ids: number[]) => {
      if (!ids?.length || !reviewerList?.length) return;
      const idSet = new Set(ids.map(Number));
      const names = reviewerList
        .filter((r) => idSet.has(Number(r.reviewerID)))
        .map((r) => (r.reviewerName ?? '').trim())
        .filter(Boolean);
      setSelectedReviewers(names);
    },
    [reviewerList]
  );

  const childNodes = useMemo(() => {
    return (
      (assignments?.flatMap((p) => p.children ?? []) as Array<
        Required<TreeViewBaseItem<ExtendedTreeItemProps>>
      >) ?? []
    );
  }, []);

  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<string[]>(
    []
  );
  const selectedAssignments: AssignmentRPW[] = useMemo(() => {
    return childNodes
      .filter((child) => selectedAssignmentIds.includes(String(child.id)))
      .map((child) => ({
        assignmentID: parseInt(String(child.id).replace(/\D/g, ''), 10) || 0,
        assignmentName: String(child.label),
      }));
  }, [childNodes, selectedAssignmentIds]);

  const handleSelectedAssignments = (ids: string[] | null) => {
    setSelectedAssignmentIds(ids ?? []);
  };

  useEffect(() => {
    if (!showDrawer) return;
    refetch();
    setStartDate(toSelectedDateArray(normalized.fromDate));
    setEndDate(toSelectedDateArray(normalized.toDate));
    setReviewerOption(getInitialReviewerOption(normalized));
    setReviewerFilterOptionId(normalized.reviewerFilterOptionId ?? 0);
    if ((normalized.reviewers?.length ?? 0) > 0 && reviewerList.length > 0) {
      prefillReviewerNamesFromIds(normalized.reviewers);
    } else {
      setSelectedReviewers([]);
    }
    const initialAssignmentIds =
      (normalized.assignments ?? []).map((a) => String(a)) ?? [];
    setSelectedAssignmentIds(initialAssignmentIds);
  }, [
    showDrawer,
    refetch,
    normalized,
    reviewerList.length,
    prefillReviewerNamesFromIds,
  ]);

  useEffect(() => {
    if (
      showDrawer &&
      reviewerOption === 'specificReviewers' &&
      (normalized.reviewers?.length ?? 0) > 0 &&
      reviewerList.length > 0
    ) {
      prefillReviewerNamesFromIds(normalized.reviewers);
    }
  }, [
    showDrawer,
    reviewerOption,
    normalized.reviewers,
    reviewerList.length,
    prefillReviewerNamesFromIds,
  ]);

  useEffect(() => {
    if (reviewerOption === 'completeDocs') {
      setReviewerFilterOptionId(6);
      setSelectedReviewers([]);
    } else if (reviewerOption === 'todoDocs') {
      setReviewerFilterOptionId(5);
      setSelectedReviewers([]);
    } else {
      setReviewerFilterOptionId(0);
    }
  }, [reviewerOption]);

  const handleCheckboxChange = (name: string) => {
    const targetName = (name ?? '').trim();
    if (!targetName) return;
    setSelectedReviewers((prev) =>
      prev.includes(targetName)
        ? prev.filter((n) => n !== targetName)
        : [...prev, targetName]
    );
  };

  const isReviewerOption = (v: string): v is ReviewerOption =>
    v === 'todoDocs' || v === 'completeDocs' || v === 'specificReviewers';

  const handleReviewerOptionChange = (value: string) => {
    if (!isReviewerOption(value)) return;
    if (
      value === 'specificReviewers' &&
      selectedReviewers.length === 0 &&
      (normalized.reviewers?.length ?? 0) > 0
    ) {
      prefillReviewerNamesFromIds(normalized.reviewers);
    }
    setReviewerOption(value);
  };

  const {mutate: saveReviewerProductivity} = useSaveReviewerProductivity(() => {
    handleDrawer(false);
  });

  const handleSave = () => {
    const fromDateStr = selectedDateArrayToApi(startDate);
    const toDateStr = selectedDateArrayToApi(endDate);
    const reviewerIdsForPayload: number[] = (reviewerList ?? [])
      .filter((r) => selectedReviewers.includes((r.reviewerName ?? '').trim()))
      .map((r) => Number(r.reviewerID))
      .filter((id) => !Number.isNaN(id));
    const assignmentIdsForPayload: number[] = selectedAssignments
      .map((a) => Number(a.assignmentID))
      .filter((id) => !Number.isNaN(id) && id >= 0);

    const payload: ReviewProductivityRequest = {
      assignments: assignmentIdsForPayload,
      reviewers: reviewerIdsForPayload,
      reviewerFilterOptionId,
      fromDate: fromDateStr,
      toDate: toDateStr,
      widgetId: normalized.widgetId,
    };

    saveReviewerProductivity(payload, {
      onSuccess: (data) => {
        onSuccess(data);
      },
    });
  };

  const contentBody = (
    <Box
      className="customize-reviewer-productivity-widget-container"
      data-testid="crpw-container"
    >
      <Box data-testid="crpw-header">
        <WidgetsHeader
          mainTitle={t('dashboard.widgetCustomization.title')}
          subTitle={t(
            'dashboard.widgetCustomization.reviewerProductivity.title'
          )}
          isCancelDisabled={false}
          onSave={handleSave}
          onCancel={() => handleDrawer(false)}
        />
      </Box>
      <Box className="content-box" data-testid="crpw-content-box">
        <Typography
          className="typo-text subhead4_size"
          data-testid="crpw-assignment-title"
        >
          {t(
            'dashboard.widgetCustomization.reviewerProductivity.selectAssignments'
          )}
        </Typography>

        {assignments && (
          <Box className="small-box" data-testid="crpw-tree-view-wrapper">
            <ArrTreeView
              itemsList={assignments}
              selectedItem={''}
              allowGlobalSelect={false}
              treeSelectedItems={selectedAssignmentIds}
              multipleSelection={true}
              isShowCountLabel={true}
              isShowCheckbox={true}
              showActions={true}
              customContentHeight={56}
              setSelectedItem={handleSelectedAssignments}
              defaultExpandedIds={
                selectedAssignmentIds.length
                  ? ['-1', ...selectedAssignmentIds]
                  : []
              }
            />
          </Box>
        )}
        {/* Date Range */}
        <Typography
          className="typo-text subhead4_size"
          data-testid="crpw-date-range-title"
        >
          {t('dashboard.widgetCustomization.reviewerProductivity.dateRange')}
        </Typography>

        <Box
          className="date-range-section small-box date-picker-gap"
          data-testid="date-range-selection"
        >
          <Box data-testid="crpw-start-date-picker-wrapper" sx={{minWidth: 0}}>
            <ArrDatePicker
              type="date-picker"
              label={t('datePicker.startDateLabel')}
              placeholder={t('datePicker.placeholder')}
              onChange={(v) => setStartDate(pickerValueToSelectedDateArray(v))}
              showToday={true}
              selectedDate={startDate}
              inputClass="date-range-customize"
              dataTestId="customize-reviewer-productivity-widget-start-date-label-button"
            />
          </Box>
          <Box className="separator-container">
            <Typography
              className="date-separator"
              data-testid={`customize-reviewer-productivity-widget-`}
            >
              —
            </Typography>
          </Box>

          <Box data-testid="crpw-end-date-picker-wrapper" sx={{minWidth: 0}}>
            <ArrDatePicker
              type="date-picker"
              label={t('datePicker.endDateLabel')}
              placeholder={t('datePicker.placeholder')}
              onChange={(v) => setEndDate(pickerValueToSelectedDateArray(v))}
              showToday={true}
              selectedDate={endDate}
              inputClass="date-range-customize"
              dataTestId="customize-reviewer-productivity-widget-end-date-label-button"
            />
          </Box>
        </Box>
        <Box data-testid="crpw-reviewer-section-wrapper">
          <WidgetsCustomizationReviewerSection
            reviewerOption={reviewerOption}
            onReviewerOptionChange={handleReviewerOptionChange}
            reviewers={reviewerList ?? []}
            selectedReviewers={selectedReviewers}
            onToggleReviewer={handleCheckboxChange}
            labels={{
              toDo: t(
                'dashboard.widgetCustomization.reviewerProductivitySection.toDo'
              ),
              completeDocs: t(
                'dashboard.widgetCustomization.reviewerProductivitySection.completeDocs'
              ),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
  return (
    <>
      {showDrawer && (
        <Box data-testid="crpw-drawer-host">
          <ArrDrawer
            showButton={false}
            isOpen={showDrawer}
            size="small"
            side="right"
            onClose={() => handleDrawer(false)}
            showHeader={false}
          >
            {contentBody}
          </ArrDrawer>
        </Box>
      )}
    </>
  );
};
